import { AutoShell as AppShell } from "@/components/Nav";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  TrendingUp,
  Wallet,
  Calendar,
  Layers,
  Sparkles,
  ShieldCheck,
  Zap,
  ArrowUpRight,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Target,
  ChevronDown,
} from "lucide-react";

const SECTIONS = [
  {
    id: "utilization",
    icon: Wallet,
    weight: "30%",
    title: "Credit Utilization",
    hook: "The single fastest lever. Move here first.",
    tips: [
      {
        h: "Keep utilization under 30%. Under 10% is elite.",
        b: "FICO rewards low utilization on both individual cards and total revolving debt. A card with a $5,000 limit should carry a statement balance under $500 for a max-score effect. Pay it down BEFORE the statement date, not the due date — that's when the bureau snapshot is taken.",
      },
      {
        h: "Pay twice a month.",
        b: "One payment right before your statement closes (this is the balance that gets reported), one payment before the due date (to avoid interest). This single habit can be worth 20–60 FICO points on its own.",
      },
      {
        h: "Ask for a credit limit increase every 6 months.",
        b: "A higher limit with the same spend = lower utilization = higher score. Most issuers do this via soft pull online. Chase, Amex, Discover, Capital One all support it. Don't accept increases that trigger a hard pull unless you're prepared for the temporary ding.",
      },
      {
        h: "Never close your oldest card.",
        b: "Closing a card cuts your total available credit AND hurts your average account age. If it's a no-annual-fee card, keep it open with a small recurring charge (Netflix, a $5/mo subscription) and autopay. Even a card you don't use is silently boosting your score.",
      },
    ],
  },
  {
    id: "payment-history",
    icon: Calendar,
    weight: "35%",
    title: "Payment History",
    hook: "The biggest single factor. One miss can cost 100+ points.",
    tips: [
      {
        h: "Autopay minimum. Manually pay the rest.",
        b: "Autopay the minimum on every card so you can NEVER be late. Then manually pay it down before the statement closes to control what gets reported. This is the professional's playbook.",
      },
      {
        h: "30 days late is the cliff.",
        b: "Being 1–29 days late costs you fees but not credit. Once you cross 30 days, it hits your report and stays for 7 years. If you're going to miss, call the creditor BEFORE day 30 and negotiate a payment plan — most will keep it out of your file if you catch it early.",
      },
      {
        h: "Ask for a Goodwill Adjustment on old lates.",
        b: "If you have a late payment from 6+ months ago and you've been on-time since, write a polite Goodwill letter asking the creditor to remove it as a courtesy. Success rate is ~20–30%, and it costs nothing to try. Aura can draft the letter for you.",
      },
      {
        h: "Never let a collection age past 6 months without a dispute.",
        b: "Collections are the fastest-decaying disputes if you're aggressive. The debt buyer typically doesn't have the original documentation. A validation letter under FCRA §1681i(a)(3) forces them to produce records they usually can't. Delete rate: 40–60%.",
      },
    ],
  },
  {
    id: "credit-age",
    icon: Layers,
    weight: "15%",
    title: "Credit Age",
    hook: "Time you can't rush. But you can hack it.",
    tips: [
      {
        h: "Get added as an Authorized User on an old, low-utilization card.",
        b: "The full history of that card grafts onto your report — instantly. A 10-year-old Amex Gold with 0% utilization can raise a thin file&rsquo;s score by 40–80 points in one statement cycle. Best route: ask a family member with a long-standing, low-utilization card to add you as an authorized user (they don&rsquo;t give you the card; you just inherit the history).",
      },
      {
        h: "Freeze the age you already have. Don't close cards.",
        b: "Every card you close accelerates the aging of your entire file. A 15-year-old card closed today doesn't fall off for 10 more years — but the moment it does, your average age crashes. Keep the old cards alive with $1/mo charges + autopay.",
      },
      {
        h: "Open your primary card as YOUNG as possible.",
        b: "If you're building for a kid, a spouse, or yourself: the day you turn 18 (or a parent adds you as AU at any age) is the most valuable day of your credit life. Every year that card exists is another year of file-age you can't buy back.",
      },
    ],
  },
  {
    id: "credit-mix",
    icon: Layers,
    weight: "10%",
    title: "Credit Mix",
    hook: "Diversity signals maturity to FICO.",
    tips: [
      {
        h: "Have at least one revolving + one installment account.",
        b: "Revolving = credit cards. Installment = car loan, mortgage, student loan, personal loan. Having both tells FICO you can handle different kinds of debt. If you're all cards, a small $500 credit-builder loan from a credit union costs ~$50 in interest and can add 10–20 points over 12 months.",
      },
      {
        h: "Store cards count, but don't chase them.",
        b: "The Home Depot / Target / Kohl's cards count as revolving credit. If you can get one without a hard pull, take it and never use it. But don't apply for 5 of them for the score — hard pulls stack up.",
      },
    ],
  },
  {
    id: "new-credit",
    icon: Sparkles,
    weight: "10%",
    title: "New Credit",
    hook: "Apply strategically. Never in clusters.",
    tips: [
      {
        h: "Space hard pulls 6 months apart.",
        b: "Each hard inquiry knocks 3–5 points off and stays visible for 2 years (though it only impacts scoring for 12 months). Multiple pulls in 90 days signal desperation to FICO. Apply for one card, wait, apply for the next.",
      },
      {
        h: "Rate-shop mortgages, autos, and student loans within a 14-day window.",
        b: "FICO treats all mortgage/auto/student loan pulls within a 14-day window as ONE inquiry. So when you shop for a car loan across 6 dealers, do it all in one weekend, not spread over a month.",
      },
      {
        h: "Use pre-approval tools that do soft pulls.",
        b: "Every major issuer (Amex, Chase, Discover, Capital One, Bank of America) has a pre-approval / offers tool that soft-pulls you. Check these before you formally apply. Only apply where you're pre-approved. This alone can save you 5–15 points a year.",
      },
    ],
  },
  {
    id: "building-scratch",
    icon: Target,
    title: "Building from scratch (no credit history)",
    hook: "0 → 720 in about 18 months. Real playbook.",
    tips: [
      {
        h: "Month 1: Secured card + credit-builder loan.",
        b: "Open a secured card at Capital One, Discover, or your local credit union. Deposit $200. Also open a credit-builder loan for $500 at your credit union. Never carry a balance on the card past the statement close. That's it. Do nothing else for 6 months.",
      },
      {
        h: "Month 6: Ask to graduate the secured card.",
        b: "Capital One and Discover both graduate secured cards to unsecured after ~6 months of on-time payments. You get your deposit back and your credit history stays intact.",
      },
      {
        h: "Month 6: Get added as AU on a family member's aged card.",
        b: "If a parent or spouse has a 5+ year old, low-utilization card, ask them to add you. The full history grafts onto your file the next statement cycle. This alone can jump a thin file 60–100 points.",
      },
      {
        h: "Month 12: Apply for one starter unsecured card.",
        b: "Discover it Chrome, Capital One Quicksilver, or a credit union cash-back card. Nothing fancy. Use it for $10 in gas per month, autopay in full.",
      },
      {
        h: "Month 18: You should be at 700+.",
        b: "3+ tradelines, 1 installment loan (auto-paid off or paying down), 12–18 months of history, 0% reported utilization. That's a solid 700–740 FICO. Time to apply for the mortgage/auto/premium card you actually want.",
      },
    ],
  },
  {
    id: "rebuilding",
    icon: ShieldCheck,
    title: "Rebuilding after damage (bankruptcy, collections, charge-offs)",
    hook: "180 days of aggressive discipline changes everything.",
    tips: [
      {
        h: "Pull all 3 bureau reports. Free. Weekly.",
        b: "annualcreditreport.com — permanent free weekly access post-COVID. Pull all 3. Compare them. Anything on Equifax but not Experian is a dispute goldmine (inconsistent reporting is per se inaccurate).",
      },
      {
        h: "Dispute EVERY negative item. All at once. Certified mail.",
        b: "Blast all 3 bureaus simultaneously with certified-mail disputes for every derogatory. About 30–50% will be deleted from at least one bureau simply because the furnisher doesn't respond in 30 days. Aura automates this end-to-end.",
      },
      {
        h: "Never pay a collection without a Pay-for-Delete agreement in writing.",
        b: "Paying a collection does NOT remove it. Get the debt collector to agree IN WRITING that they will delete the tradeline from all 3 bureaus upon payment. If they won't put it in writing, don't pay. Dispute it instead.",
      },
      {
        h: "Add positive tradelines while you dispute.",
        b: "You can't just remove negatives — you have to overwrite them with positive history. Secured card + AU tradeline + credit-builder loan running in parallel with your dispute campaign. Removal + rebuild simultaneously.",
      },
      {
        h: "Bankruptcy: it doesn't kill you. It resets you.",
        b: "A Chapter 7 falls off in 10 years, Chapter 13 in 7. Meanwhile, most people who file bankruptcy have a 640+ score within 24 months if they follow the playbook above. Bankruptcy is a floor, not a ceiling.",
      },
    ],
  },
  {
    id: "advanced",
    icon: Zap,
    title: "Advanced moves (once you're 700+)",
    hook: "The optimizations most people never learn.",
    tips: [
      {
        h: "Optimize the AZEO trick (All Zero Except One).",
        b: "Right before applying for a mortgage or auto loan, pay ALL your cards to $0 statement balance except ONE, which you leave at 1–9% utilization. This mathematically maximizes FICO 8 score. Worth 20–40 points on application day.",
      },
      {
        h: "Freeze your credit at all 3 bureaus PLUS ChexSystems and LexisNexis.",
        b: "Free at all 5. Blocks identity theft, prevents unauthorized accounts, and doesn't hurt your score. Unfreezing takes 60 seconds when you actually need to apply.",
      },
      {
        h: "Use business credit cards to hide utilization.",
        b: "Most business cards (Chase Ink, Amex Business, Capital One Spark) don't report to your personal credit report — so business spend doesn't inflate your personal utilization. Perfect for entrepreneurs.",
      },
      {
        h: "Get to 3 revolving + 1 installment + 0 late = FICO 800+.",
        b: "You don't need 20 cards. Three well-aged revolving accounts + one installment loan + zero derogatories over 24 months = 800+ FICO. Simplicity wins.",
      },
    ],
  },
];

function Section({ s, expanded, onToggle }) {
  const Icon = s.icon;
  return (
    <div className="card-surface overflow-hidden" data-testid={`guide-section-${s.id}`}>
      <button
        onClick={onToggle}
        data-testid={`guide-toggle-${s.id}`}
        className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-[#17191C] transition-colors"
      >
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/20 flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-[#D5FF3F]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <div className="font-display text-2xl leading-tight">{s.title}</div>
              {s.weight && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F]/15 text-[#D5FF3F]">
                  {s.weight} of FICO
                </span>
              )}
            </div>
            <div className="text-sm text-[#A8ADA5]">{s.hook}</div>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-[#6D716C] flex-shrink-0 mt-2 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="px-6 pb-6 space-y-4 border-t border-[#26282C] pt-5">
          {s.tips.map((t, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-[#7CFF9B] flex-shrink-0 mt-1" />
              <div>
                <div className="font-medium text-[#F5F3EE] mb-1">{t.h}</div>
                <p className="text-sm text-[#A8ADA5] leading-relaxed">{t.b}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CreditGuide() {
  const [expanded, setExpanded] = useState({ utilization: true }); // open the biggest lever by default

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <AppShell>
      <SEO
        title="Credit Playbook — 30+ tactics to build & rebuild your FICO"
        description="The complete Aura credit playbook. FICO anatomy explained, utilization tricks, dispute strategies, tradelines, rebuilding after bankruptcy, and advanced 700+ moves. No fluff."
        path="/playbook"
        type="article"
        keywords="credit repair guide, how to raise credit score, credit utilization, FICO score, tradelines, credit rebuilding, dispute strategy, pay for delete"
      />
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">The playbook</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Everything we know
          <br />
          <span className="text-[#D5FF3F]">about credit.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl leading-relaxed">
          No fluff, no affiliate hustle, no &ldquo;sign up to see more.&rdquo; Just the moves that actually
          work &mdash; ranked by how much they impact your FICO. Read it, use it, share it.
        </p>
      </div>

      {/* Score anatomy strip */}
      <div className="card-surface p-6 mb-8" data-testid="fico-anatomy">
        <div className="overline mb-4">FICO anatomy — where the points come from</div>
        <div className="grid grid-cols-5 gap-2">
          {[
            { pct: 35, label: "Payment history", color: "bg-[#D5FF3F]" },
            { pct: 30, label: "Utilization", color: "bg-[#7CFF9B]" },
            { pct: 15, label: "Age of accounts", color: "bg-[#A8ADA5]" },
            { pct: 10, label: "Credit mix", color: "bg-[#6D716C]" },
            { pct: 10, label: "New credit", color: "bg-[#40433E]" },
          ].map((b) => (
            <div key={b.label}>
              <div
                className={`${b.color} rounded-sm`}
                style={{ height: `${b.pct * 2 + 20}px` }}
              />
              <div className="ticker text-lg text-[#F5F3EE] mt-2">{b.pct}%</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] leading-tight">
                {b.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4 mb-12" data-testid="guide-sections">
        {SECTIONS.map((s) => (
          <Section key={s.id} s={s} expanded={!!expanded[s.id]} onToggle={() => toggle(s.id)} />
        ))}
      </div>

      {/* CTA */}
      <div className="card-surface p-8 md:p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D5FF3F]/10 blur-[100px] pointer-events-none" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1.5 mb-6">
            <Sparkles size={12} className="text-[#D5FF3F]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D5FF3F]">
              Put the playbook to work
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl leading-none mb-4">
            Ready to run it?
          </h2>
          <p className="text-[#A8ADA5] mb-6 leading-relaxed">
            Upload your report and Aura will map every one of these moves against your actual file
            &mdash; ranked by how many points each is worth for you specifically.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/app/scan">
              <Button className="rounded-full btn-lime h-12 px-6" data-testid="guide-scan-cta">
                Run AI Scan <ArrowUpRight size={15} className="ml-1.5" />
              </Button>
            </Link>
            <Link to="/app/tips">
              <Button className="rounded-full btn-outline h-12 px-6" data-testid="guide-coach-cta">
                <Sparkles size={14} className="mr-2" /> See ranked moves
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Sources footer */}
      <div className="mt-8 pt-6 border-t border-[#26282C] flex items-start gap-3 text-xs text-[#6D716C]">
        <AlertCircle size={14} className="text-[#6D716C] flex-shrink-0 mt-0.5" />
        <div className="font-mono leading-relaxed">
          Sources: FICO Score composition (myfico.com), FCRA §§1681-1681x, Consumer Financial
          Protection Bureau publications, Experian &amp; Equifax public documentation. This is
          educational content, not personalized financial advice. See our{" "}
          <Link to="/app/disclaimer" className="text-[#D5FF3F] underline">
            disclaimer
          </Link>
          .
        </div>
      </div>
    </AppShell>
  );
}
